/**
 * Map View Wiring - Event handlers and control wiring for map view
 *
 * Extracted from navigateToMapView() to improve maintainability.
 * These functions wire up all the interactive elements in the map view.
 */

import { CONFIG } from '../config.js';
import { sessionManager } from './session-manager.js';

/**
 * Wire up map controls (zoom in/out, reset view)
 * @param {HTMLElement} hexMap - The hex-map element
 */
export function wireMapControls(hexMap) {
  const zoomInBtn = document.querySelector('.map-controls button[title="Zoom In"]');
  const zoomOutBtn = document.querySelector('.map-controls button[title="Zoom Out"]');
  const resetBtn = document.querySelector('.map-controls button[title="Reset View"]');

  if (zoomInBtn) {
    zoomInBtn.addEventListener('click', () => {
      if (hexMap && hexMap.zoom) {
        hexMap.zoom(CONFIG.ZOOM.IN_FACTOR);
      }
    });
  }

  if (zoomOutBtn) {
    zoomOutBtn.addEventListener('click', () => {
      if (hexMap && hexMap.zoom) {
        hexMap.zoom(CONFIG.ZOOM.OUT_FACTOR);
      }
    });
  }

  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      if (hexMap && hexMap.resetView) {
        hexMap.resetView();
      }
    });
  }
}

/**
 * Wire up home button to return to landing page
 */
export function wireHomeButton() {
  const homeBtn = document.getElementById('home-btn');
  if (homeBtn) {
    homeBtn.addEventListener('click', () => {
      window.location.href = '/';
    });
  }
}

/**
 * Wire up player view button to open Player View window
 * @param {Function} broadcastSessionState - Function to broadcast session state
 */
export function wirePlayerViewButton(broadcastSessionState) {
  const playerViewBtn = document.getElementById('player-view-btn');
  if (playerViewBtn) {
    playerViewBtn.addEventListener('click', () => {
      // Get current session from URL to include in player view URL
      const urlParams = new URLSearchParams(window.location.search);
      const sessionParam = urlParams.get('session');
      const playerUrl = sessionParam ? `?view=player&session=${sessionParam}` : '?view=player';

      window.open(playerUrl, 'player-view', 'width=1200,height=800');

      // Broadcast current session state to the newly opened Player View
      setTimeout(() => {
        broadcastSessionState();
      }, CONFIG.BROADCAST_DELAY);
    });
  }
}

/**
 * Wire up calendar button to show calendar view
 * @param {Function} showCalendarView - Function to show calendar view
 */
export function wireCalendarButton(showCalendarView) {
  const calendarBtn = document.getElementById('calendar-btn');
  if (calendarBtn) {
    calendarBtn.addEventListener('click', () => {
      showCalendarView();
    });
  }
}

/**
 * Wire up display ID toggle button
 * @param {HTMLElement} hexMap - The hex-map element
 */
export function wireDisplayIdToggle(hexMap) {
  const toggleIdsBtn = document.getElementById('toggle-ids-btn');
  if (toggleIdsBtn) {
    toggleIdsBtn.addEventListener('click', () => {
      const session = sessionManager.getSession();
      if (session) {
        session.showDisplayIds = !session.showDisplayIds;
        if (hexMap && hexMap.toggleDisplayIds) {
          hexMap.toggleDisplayIds(session.showDisplayIds);
        }
      }
    });
  }
}

/**
 * Wire up refresh map button
 * @param {HTMLElement} hexMap - The hex-map element
 */
export function wireRefreshMapButton(hexMap) {
  const refreshMapBtn = document.getElementById('refresh-map-btn');
  if (refreshMapBtn) {
    refreshMapBtn.addEventListener('click', async () => {
      const session = sessionManager.getSession();
      const mapData = sessionManager.getMapData();
      if (hexMap && session && mapData) {
        await hexMap.loadMap(session, mapData);
      }
    });
  }
}

/**
 * Wire up time travel controls (slider, buttons, bookmark jump)
 * @param {Object} params - Parameters object
 * @param {Object} params.session - Current session
 * @param {Function} params.setTimeTravelDay - Function to set time travel day
 * @param {Function} params.jumpToPresent - Function to jump to present
 * @param {Function} params.populateBookmarkDropdown - Function to populate bookmark dropdown
 */
export function wireTimeTravelControls({ session, setTimeTravelDay, jumpToPresent, populateBookmarkDropdown }) {
  const timeTravelToggleBtn = document.getElementById('time-travel-toggle-btn');
  const timeTravelSliderContainer = document.getElementById('time-travel-slider');
  const timeSlider = document.getElementById('time-slider');
  const timeDisplay = document.getElementById('time-display');
  const jumpToPresentBtn = document.getElementById('jump-to-present-btn');
  const timePrevBtn = document.getElementById('time-prev-btn');
  const timeNextBtn = document.getElementById('time-next-btn');

  if (!timeTravelToggleBtn || !timeTravelSliderContainer || !timeSlider) return;

  // Initialize slider max value to current days elapsed
  const currentDaysElapsed = session.calendar?.daysElapsed || 1;
  timeSlider.max = currentDaysElapsed;
  timeSlider.value = currentDaysElapsed;

  // Toggle slider visibility
  timeTravelToggleBtn.addEventListener('click', () => {
    const isVisible = timeTravelSliderContainer.style.display === 'block';
    timeTravelSliderContainer.style.display = isVisible ? 'none' : 'block';

    // Reset to current time when hiding
    if (isVisible) {
      timeSlider.value = currentDaysElapsed;
      setTimeTravelDay(currentDaysElapsed);
    } else {
      // Populate bookmark dropdown when showing
      populateBookmarkDropdown();
    }
  });

  // Handle slider changes for time travel with RAF throttling for smooth scrubbing
  let timeTravelRafId = null;
  let pendingTimeTravelDay = null;

  timeSlider.addEventListener('input', (e) => {
    const selectedDay = parseInt(e.target.value);
    pendingTimeTravelDay = selectedDay;

    // Update day display immediately for responsive feedback
    if (timeDisplay) {
      timeDisplay.textContent = `Day ${selectedDay}`;
    }

    // Throttle expensive updates (map refresh, broadcast) using requestAnimationFrame
    if (timeTravelRafId !== null) {
      cancelAnimationFrame(timeTravelRafId);
    }

    timeTravelRafId = requestAnimationFrame(() => {
      if (pendingTimeTravelDay !== null) {
        setTimeTravelDay(pendingTimeTravelDay);
        timeTravelRafId = null;
        pendingTimeTravelDay = null;
      }
    });
  });

  // Handle Present button click
  if (jumpToPresentBtn) {
    jumpToPresentBtn.addEventListener('click', () => {
      jumpToPresent();
    });
  }

  // Handle Prev/Next button clicks for day-by-day navigation
  if (timePrevBtn) {
    timePrevBtn.addEventListener('click', () => {
      const currentDay = parseInt(timeSlider.value);
      const newDay = Math.max(1, currentDay - 1);
      if (newDay !== currentDay) {
        timeSlider.value = newDay;
        timeDisplay.textContent = `Day ${newDay}`;
        setTimeTravelDay(newDay);
      }
    });
  }

  if (timeNextBtn) {
    timeNextBtn.addEventListener('click', () => {
      const currentDay = parseInt(timeSlider.value);
      const maxDay = parseInt(timeSlider.max);
      const newDay = Math.min(maxDay, currentDay + 1);
      if (newDay !== currentDay) {
        timeSlider.value = newDay;
        timeDisplay.textContent = `Day ${newDay}`;
        setTimeTravelDay(newDay);
      }
    });
  }
}

/**
 * Wire up date dropdown menu
 * @param {Function} advanceTime - Function to advance time by days
 * @param {Function} showCalendarView - Function to show calendar view
 */
export function wireDateDropdown(advanceTime, showCalendarView) {
  const currentDateDisplay = document.getElementById('current-date-display');
  const dateDropdown = document.getElementById('date-dropdown');
  const openCalendarBtn = document.getElementById('open-calendar-from-dropdown');

  if (!currentDateDisplay || !dateDropdown) return;

  // Toggle dropdown on click
  currentDateDisplay.addEventListener('click', () => {
    dateDropdown.style.display = dateDropdown.style.display === 'none' ? 'block' : 'none';
  });

  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (!currentDateDisplay.contains(e.target) && !dateDropdown.contains(e.target)) {
      dateDropdown.style.display = 'none';
    }
  });

  // Handle advance date buttons
  dateDropdown.querySelectorAll('.date-menu-item[data-advance]').forEach(btn => {
    btn.addEventListener('click', () => {
      const days = parseInt(btn.dataset.advance);
      advanceTime(days);
      dateDropdown.style.display = 'none';
    });
  });

  // Handle open calendar button
  if (openCalendarBtn) {
    openCalendarBtn.addEventListener('click', () => {
      showCalendarView();
      dateDropdown.style.display = 'none';
    });
  }
}

/**
 * Wire up save, undo, and redo buttons
 * @param {Object} params - Parameters object
 * @param {Function} params.handleSaveSession - Function to save session
 * @param {Function} params.handleUndo - Function to handle undo
 * @param {Function} params.handleRedo - Function to handle redo
 */
export function wireSaveUndoRedo({ handleSaveSession, handleUndo, handleRedo }) {
  const saveBtn = document.getElementById('save-btn');
  const undoBtn = document.getElementById('undo-btn');
  const redoBtn = document.getElementById('redo-btn');

  console.log('[wireSaveUndoRedo] Buttons found:', {
    saveBtn: !!saveBtn,
    undoBtn: !!undoBtn,
    redoBtn: !!redoBtn
  });

  if (saveBtn) {
    saveBtn.addEventListener('click', handleSaveSession);
  }

  if (undoBtn) {
    console.log('[wireSaveUndoRedo] Wiring undo button');
    undoBtn.addEventListener('click', () => {
      console.log('[wireSaveUndoRedo] Undo button CLICKED');
      handleUndo();
    });
  }

  if (redoBtn) {
    console.log('[wireSaveUndoRedo] Wiring redo button');
    redoBtn.addEventListener('click', () => {
      console.log('[wireSaveUndoRedo] Redo button CLICKED');
      handleRedo();
    });
  }
}

/**
 * Wire up print button
 * @param {Function} showPrintModal - Function to show print modal
 */
export function wirePrintButton(showPrintModal) {
  const printBtn = document.getElementById('print-btn');
  if (printBtn) {
    printBtn.addEventListener('click', showPrintModal);
  }
}

/**
 * Wire up auto-save button and functionality
 * @param {Object} params - Parameters object
 * @param {Object} params.autoSaveManager - AutoSaveManager instance
 * @param {Function} params.getSession - Function to get current session
 * @param {Function} params.getMapData - Function to get current map data
 */
export function wireAutoSaveButton({ autoSaveManager, getSession, getMapData }) {
  const autoSaveBtn = document.getElementById('auto-save-btn');
  const autoSaveText = document.getElementById('auto-save-text');
  const autoSaveIndicator = document.getElementById('auto-save-indicator');
  const autoSaveStatus = document.getElementById('auto-save-status');

  if (!autoSaveBtn) return; // Auto-save not available (not Chromium or demo session)

  // Import auto-save utilities dynamically
  import('./file-auto-save.js').then(async (autoSave) => {
    // Check if file is already linked
    const status = await autoSave.getAutoSaveStatus();
    if (status.linked) {
      updateButtonState(true, status.fileName);
      autoSaveManager.enable();
    }

    // Handle button click
    autoSaveBtn.addEventListener('click', async () => {
      const isLinked = autoSaveBtn.dataset.linked === 'true';

      if (isLinked) {
        // Unlink file
        if (confirm('Unlink auto-save file? Manual saves will still work.')) {
          await autoSave.unlinkAutoSaveFile();
          updateButtonState(false);
          autoSaveManager.disable();
        }
      } else {
        // Link file
        const session = getSession();
        const suggestedName = `${session.mapName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-session.json`;
        const fileHandle = await autoSave.initializeAutoSave(suggestedName);

        if (fileHandle) {
          updateButtonState(true, fileHandle.name);
          autoSaveManager.enable();

          // Perform initial save
          const mapData = getMapData();
          await autoSaveManager.forceSave(session, mapData);
        }
      }
    });

    // Listen for auto-save events
    document.addEventListener('auto-save-complete', (event) => {
      showSaveIndicator();
    });

    // Update button state
    function updateButtonState(linked, fileName = null) {
      autoSaveBtn.dataset.linked = linked;
      if (linked) {
        autoSaveBtn.title = `Linked to: ${fileName || 'file'}\nClick to unlink`;
        autoSaveText.textContent = fileName || 'Linked';
        autoSaveBtn.classList.add('btn-success');
      } else {
        autoSaveBtn.title = 'Link Auto-Save File';
        autoSaveText.textContent = 'Link File';
        autoSaveBtn.classList.remove('btn-success');
      }
    }

    // Show save indicator briefly
    function showSaveIndicator() {
      if (!autoSaveIndicator) return;

      const now = new Date();
      const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      autoSaveStatus.textContent = `Auto-saved at ${timeStr}`;

      autoSaveIndicator.style.display = 'inline-block';
      setTimeout(() => {
        autoSaveIndicator.style.display = 'none';
      }, 3000);
    }
  });
}

/**
 * Wire up utilities menu (settings dropdown)
 * @param {Object} handlers - Object with handler functions
 * @param {Function} handlers.syncTiles - Function to sync tiles
 * @param {Function} handlers.showIconAttribution - Function to show icon attribution
 */
export function wireUtilitiesMenu({ syncTiles, showIconAttribution }) {
  const settingsBtn = document.getElementById('settings-btn');
  const utilitiesDropdown = document.getElementById('utilities-dropdown');

  if (!settingsBtn || !utilitiesDropdown) return;

  // Toggle dropdown
  settingsBtn.addEventListener('click', () => {
    utilitiesDropdown.style.display =
      utilitiesDropdown.style.display === 'none' ? 'block' : 'none';
  });

  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (!settingsBtn.contains(e.target) && !utilitiesDropdown.contains(e.target)) {
      utilitiesDropdown.style.display = 'none';
    }
  });

  // Handle menu item clicks
  utilitiesDropdown.querySelectorAll('.utilities-menu-item').forEach(item => {
    item.addEventListener('click', () => {
      const action = item.dataset.action;
      if (action === 'sync-tiles') {
        syncTiles();
      } else if (action === 'icon-attribution') {
        showIconAttribution();
      }
      utilitiesDropdown.style.display = 'none';
    });
  });
}
